/**
 * New-run composer. A brief textarea + attachment picker compose a
 * `StartRunBody` (the SPA never sends contractorId — the BFF injects it). On
 * Launch we drive the run through the package session via `useRunStream`; once
 * the backend assigns an execution id (surfaced on `state.executionId` from the
 * first NDJSON record), we `history.replaceState` to `/runs/:id` so a refresh
 * or share lands on the canonical run URL, then hand rendering to LiveRunView.
 *
 * The brief composer stays mounted (so the session isn't torn down); it just
 * collapses to a compact summary once the run is in flight.
 *
 * A start that fails before the service answered anything has no response for
 * LiveRunView to render, so this page says what happened (`startFailure`).
 */
import {
  Icon,
  Notice,
  type PlaygroundResumeReply,
  type SessionState,
  Spinner,
  useAttachmentUploads
} from '@evt/flow-stream-ui'
import type { RunInputView, StartRunBody } from '@portal/contracts'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { listFlows } from '../api/flows'
import { cancelRun } from '../api/runs'
import { uploadClient, useUploadLimits } from '../api/uploads'
import { AttachmentPicker } from '../components/AttachmentPicker'
import { LiveRunView } from '../components/LiveRunView'
import { RunRequest } from '../components/RunRequest'
import { keys } from '../hooks/queryClient'
import { type RunStart, useRunStream } from '../hooks/use-run-stream'
import { useI18n } from '../i18n'
import { describeApiError, UPLOAD_REFUSAL_CODES } from '../i18n/errors'

/**
 * Why a run that failed has nothing to show, or null when LiveRunView renders
 * the outcome itself (a response came back) or the run did not fail.
 *
 * - `refused`: the BFF answered the start with an error, kept in `start.refusal`.
 * - `unreachable`: no answer came back, so the run did not start.
 * - `interrupted`: the start was accepted and the stream ended without a result.
 *   The run may still be working server-side, where it survives the browser; its
 *   id is known once the first record arrived.
 */
export type StartFailure =
  | { kind: 'refused'; refusal: NonNullable<RunStart['refusal']> }
  | { kind: 'unreachable' }
  | { kind: 'interrupted'; executionId?: string }

export function startFailure(
  state: Pick<SessionState, 'status' | 'error' | 'executionId'>,
  start: RunStart
): StartFailure | null {
  if (state.status !== 'failure') return null
  if (state.error && 'response' in state.error) return null
  if (state.executionId || start.accepted) {
    return state.executionId ? { kind: 'interrupted', executionId: state.executionId } : { kind: 'interrupted' }
  }
  if (start.refusal) return { kind: 'refused', refusal: start.refusal }
  return { kind: 'unreachable' }
}

/** A run that never started has nothing to show: its stage would read as a run about to begin. */
export function showsRunView(failure: StartFailure | null): boolean {
  return failure === null || failure.kind === 'interrupted'
}

export function NewRunPage() {
  const { flowId } = useParams<{ flowId: string }>()
  const navigate = useNavigate()
  const { t } = useI18n()
  const decodedFlowId = flowId ? decodeURIComponent(flowId) : ''

  const flows = useQuery({ queryKey: keys.flows(), queryFn: listFlows })
  const flow = flows.data?.find((f) => f.flowId === decodedFlowId)
  // The list answered and this flow is not on it: the company does not have it (or it was taken away since the
  // link was made). The server refuses the launch either way; saying so here spares writing a brief for nothing.
  const unavailable = flows.isSuccess && !flow

  const { state, run, respond, cancel, reset, start } = useRunStream()
  const [brief, setBrief] = useState('')
  // Files upload in parts as they are picked (ADR-0003); the launch sends what they became.
  const limits = useUploadLimits()
  const uploads = useAttachmentUploads(uploadClient, limits)
  const [cancelling, setCancelling] = useState(false)
  const launchedAtRef = useRef<string>('')

  const launched = state.status !== 'idle'
  const failure = startFailure(state, start)

  // A launch refused because of one of its files (an upload that lapsed while the brief was written): ask the server
  // which finished uploads it still holds, so a lapsed one fails — its retry sends the file again — instead of its
  // chip claiming it is ready when the person goes back to the request.
  const { revalidate } = uploads
  useEffect(() => {
    const code = start.refusal?.issue?.code
    if (code && UPLOAD_REFUSAL_CODES.has(code)) void revalidate()
  }, [start.refusal, revalidate])

  // What was asked, from the composer's own state (the persisted input record
  // exists server-side, but this page never refetches — it owns the stream).
  const localInput = useMemo<RunInputView>(
    () => ({
      executionId: state.executionId ?? '',
      brief: brief.trim(),
      attachments: uploads.items.map((f, i) => ({ index: i, name: f.name, mimetype: f.mimetype, size: f.size })),
      replies: [],
      createdAt: launchedAtRef.current || new Date().toISOString()
    }),
    [state.executionId, brief, uploads.items]
  )

  // Mirror the assigned execution id into the URL exactly once, without a
  // router navigation (which would unmount this page and tear the session
  // down). RunView is mounted at /runs/:id, but here the live session already
  // owns the stream — replaceState keeps the address bar canonical.
  const urlSyncedRef = useRef(false)
  useEffect(() => {
    if (!urlSyncedRef.current && state.executionId) {
      urlSyncedRef.current = true
      history.replaceState(history.state, '', `/runs/${encodeURIComponent(state.executionId)}`)
    }
  }, [state.executionId])

  const onLaunch = () => {
    launchedAtRef.current = new Date().toISOString()
    const attachments = uploads.attachments
    const body: StartRunBody = {
      flowId: decodedFlowId,
      brief: brief.trim(),
      ...(attachments.length > 0 ? { attachments } : {})
    }
    // contractorId is intentionally omitted — the BFF injects it from session.
    void run(body)
  }

  const onReply = (reply: PlaygroundResumeReply) => {
    void respond({ reply, meta: { trigger: 'reply' } })
  }

  const onCancel = async () => {
    if (!state.executionId) {
      cancel()
      return
    }
    setCancelling(true)
    try {
      await cancelRun(state.executionId, { reason: 'user_cancelled' })
    } catch {
      cancel() // fall back to a client-side abort if the server cancel fails
    } finally {
      setCancelling(false)
    }
  }

  if (!decodedFlowId) {
    return (
      <div className="page portal-page">
        <Notice kind="danger">{t.newRun.missingFlowId}</Notice>
      </div>
    )
  }

  return (
    <div className="page portal-page run-page">
      <header className="page-header">
        <div>
          <button type="button" className="btn btn-ghost btn-sm run-back" onClick={() => navigate('/flows')}>
            <Icon name="chevron" size={14} /> {t.newRun.backToFlows}
          </button>
          <h1 className="page-title">{flow?.displayName ?? decodedFlowId}</h1>
          {flow?.description && <div className="page-sub">{flow.description}</div>}
        </div>
      </header>

      {!launched && flows.isPending ? (
        // Until the list answers, whether this company has the flow is unknown: no composer for a flow it may not have.
        <div className="portal-loading">
          <Spinner size={16} /> {t.common.loading}
        </div>
      ) : !launched && unavailable ? (
        <Notice kind="warning">
          {t.newRun.flowNotAvailable}{' '}
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => navigate('/flows')}>
            {t.newRun.backToFlows}
          </button>
        </Notice>
      ) : !launched ? (
        <div className="brief-composer card card-pad">
          <label className="field-label" htmlFor="brief">
            {t.newRun.briefLabel}
          </label>
          <textarea
            id="brief"
            className="textarea brief-input"
            rows={8}
            placeholder={t.newRun.briefPlaceholder}
            value={brief}
            onChange={(e) => setBrief(e.target.value)}
          />
          <AttachmentPicker uploads={uploads} limits={limits} />
          <div className="brief-actions">
            <button
              type="button"
              className="btn btn-primary"
              disabled={!brief.trim() || uploads.busy || !uploads.ready}
              onClick={onLaunch}
            >
              <Icon name="play" size={14} /> {t.newRun.launch}
            </button>
            {/* Why Launch waits: it never leaves without a file the person picked. */}
            {uploads.busy && (
              <span className="muted brief-hint" aria-live="polite">
                <Spinner size={12} /> {t.newRun.attachWaiting}
              </span>
            )}
            {flow?.maybeAsksQuestions && (
              <span className="muted brief-hint">
                <Icon name="spark" size={12} /> {t.newRun.mayPauseHint}
              </span>
            )}
          </div>
        </div>
      ) : (
        <>
          <RunRequest input={localInput} downloadable={false} />
          {failure && (
            <Notice kind={failure.kind === 'interrupted' ? 'warning' : 'danger'}>
              {failure.kind === 'refused' && (
                <>
                  {t.newRun.notStarted} {describeApiError(t, failure.refusal)}
                </>
              )}
              {failure.kind === 'unreachable' && (
                <>
                  {t.newRun.notStarted} {t.newRun.unreachable}
                </>
              )}
              {failure.kind === 'interrupted' &&
                (failure.executionId ? t.newRun.interrupted : t.newRun.interruptedEarly)}{' '}
              {failure.kind === 'interrupted' ? (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() =>
                    navigate(
                      failure.executionId ? `/runs/${encodeURIComponent(failure.executionId)}` : '/conversations'
                    )
                  }
                >
                  {failure.executionId ? t.newRun.openRun : t.newRun.openHistory}
                </button>
              ) : (
                <button type="button" className="btn btn-ghost btn-sm" onClick={reset}>
                  {t.newRun.backToRequest}
                </button>
              )}
            </Notice>
          )}
          {flows.isLoading && !flow && (
            <div className="portal-loading">
              <Spinner size={16} /> {t.common.loading}
            </div>
          )}
          {showsRunView(failure) && (
            <LiveRunView
              state={state}
              maxRounds={flow?.maxRounds}
              onReply={onReply}
              onCancel={() => void onCancel()}
              cancelling={cancelling}
            />
          )}
        </>
      )}
    </div>
  )
}
